// サウンドレビュー（固定価格サービス）の金額・挙動を集約した定数群。
//
// サービス概要:
//   本来総額（税込）10,000 円。依頼者にとって**初回のみ**ディスカウント価格（税込）6,800 円で提供し、
//   2 回目以降は本来総額で集金する。プロジェクト作成と同時に見積もりを自動発行する。
//
// お金の流れ（既存の丸めロジック Math.floor で検証済み）:
//   | 項目                       | 初回      | 2 回目以降 |
//   | 依頼者の実支払額（集金）   | 6,800 円  | 10,000 円  |
//   | プロ受取（送金額・固定）   | 8,000 円  | 8,000 円   |
//   | OLS 取り分（手数料 + 税）  | 2,000 円  | 2,000 円   |
//   | プラットフォーム負担       | 1,200 円  | 0 円       |
//
// 初回は集金 6,800 < 送金 8,000 のため、Stripe transfer 時に source_transaction を外して
// プラットフォーム残高から満額送金する（ignoreSourceTransaction）。2 回目は集金が送金を上回るため不要。
//
// 値引きクーポン（初回完了リワードの 30%オフなど）はサウンドレビューにも重ねられる。
// 集金額だけが下がり、プロ受取・OLS 取り分は上表のまま変わらないため、割引分はすべて
// プラットフォーム負担が増える形になる（例: 2 回目 10,000 円に 30%オフ → 集金 7,000 円・負担 3,000 円）。
//
// 見積もりは初回・2 回目とも「本来総額 10,000 円」で保存し、初回のディスカウントは
// Stripe Checkout の line_item を 6,800 円で直接作成して表現する（外部クーポン依存なし）。

/** サウンドレビューを一意に識別する plan slug（src/data/service.ts の SERVICE_LIST と一致） */
export const SOUND_REVIEW_SLUG = 'sound-review'

/** 見積もり amount（税抜）。本来総額 10,000 円の税抜額。getTax(9091) = 909 → total 10,000 */
export const SOUND_REVIEW_ESTIMATE_AMOUNT = 9091
/** 見積もり tax。getTax(9091) と一致 */
export const SOUND_REVIEW_ESTIMATE_TAX = 909
/** 本来総額（税込）。見積もりカードに表示する定価 */
export const SOUND_REVIEW_ESTIMATE_TOTAL = 10000

/** 初回の実集金額（税込・ディスカウント後）。Stripe Checkout の line_item 金額 */
export const SOUND_REVIEW_CHARGE_TOTAL = 6800
/** 初回の実集金額の税抜分（6800 − 618）。purchase.amount に保存 */
export const SOUND_REVIEW_CHARGE_AMOUNT = 6182
/** 初回の実集金額の消費税分（内税10%: 6800 − round(6800/1.1)）。purchase.tax に保存 */
export const SOUND_REVIEW_CHARGE_TAX = 618

/**
 * 初回限定キャンペーンの適用有無。Stripe Checkout 作成時に PaymentIntent の metadata へ刻み、
 * 決済完了時（purchase 生成）はこの値と実集金額だけを読んで金額を復元する。
 */
export type TSoundReviewCampaign = 'first' | 'none'

/** PaymentIntent metadata のキー名 */
export const SOUND_REVIEW_CAMPAIGN_METADATA_KEY = 'soundReviewCampaign'

/**
 * キャンペーン適用有無から実集金額（税抜 / 消費税 / 税込）を引く。
 * Checkout の line_item・purchase の金額・UI 表示のすべてがここを唯一の参照元にする。
 */
export const getSoundReviewCharge = (campaign: TSoundReviewCampaign) => {
  if (campaign === 'first') {
    return {
      amount: SOUND_REVIEW_CHARGE_AMOUNT,
      tax: SOUND_REVIEW_CHARGE_TAX,
      total: SOUND_REVIEW_CHARGE_TOTAL,
    }
  }
  return {
    amount: SOUND_REVIEW_ESTIMATE_AMOUNT,
    tax: SOUND_REVIEW_ESTIMATE_TAX,
    total: SOUND_REVIEW_ESTIMATE_TOTAL,
  }
}

/** プロへの送金額（固定）。集金額に関わらずプラットフォーム残高から満額送金する */
export const SOUND_REVIEW_PRO_REVENUE = 8000
/** OLS 手数料（税抜） */
export const SOUND_REVIEW_COMMISSION_FEE = 1819
/** OLS 手数料にかかる消費税 */
export const SOUND_REVIEW_COMMISSION_TAX = 181
/** OLS 取り分合計（手数料 + 税）= 2,000 */
export const SOUND_REVIEW_COMMISSION_TOTAL = 2000

/**
 * 見積もりの作業期間（日）。プロジェクト開始日からこの日数後が納期になる（デフォルト 2 週間）。
 * 面談日程は依頼時に自由入力で受け取り、実際の日時はメッセージで調整するため、
 * 納期はこの既定値で決める。※運用に合わせて調整可
 */
export const SOUND_REVIEW_WORKING_DAYS = 14

/** 自動発行される見積もりのタイトル */
export const SOUND_REVIEW_ESTIMATE_TITLE = 'サウンドレビュー'
/** 自動発行される見積もりの説明文。※運用に合わせて調整可 */
export const SOUND_REVIEW_ESTIMATE_DESCRIPTION =
  'ご自身で制作された楽曲をプロの耳で聞いていただき、フィードバックをお届けします。'

/**
 * お問い合わせモーダルで注入する仮想「サウンドレビュー」サービスの識別 id。
 * isSoundReviewPro が真のプロには実 category が無くてもこの選択肢を出す。
 * 実在の category ではないが、オファー送信時は _serviceCategoryId にこの仮想IDを載せる。
 * offer/create は仮想ID一致で serviceSlug を確定する（title 文字列のゆれに依存しない）。
 */
export const SOUND_REVIEW_VIRTUAL_CATEGORY_ID = 'sound-review-virtual'

/**
 * クリエイターが対応可能なサウンドレビューの種別（定型の選択肢）。
 * 職種（professionalCategories / categoryList）とは別軸で、「何をレビューできるか」を表す。
 * 運営（mgmt）のみが user.acceptedSoundReviewTypes に設定する。
 *
 * user.acceptedSoundReviewTypes には定型の値（このキー）と自由入力の文字列（表示名そのまま）が
 * 混在するため、表示は必ず getSoundReviewTypeLabel(s) を通すこと。
 */
export const SOUND_REVIEW_TYPES = [
  'music',
  'mix',
  'mastering',
] as const

export type TSoundReviewType = typeof SOUND_REVIEW_TYPES[number]

/** 定型の種別の表示名。DB には英語の値を保存し、画面表示ではこの対応表を使う */
export const SOUND_REVIEW_TYPE_LABELS: Record<TSoundReviewType, string> = {
  music: '楽曲レビュー',
  mix: 'ミックスレビュー',
  mastering: 'マスタリングレビュー',
}

/** 自由入力の 1 件あたりの最大文字数 */
export const SOUND_REVIEW_TYPE_MAX_LENGTH = 30

/** 保存値の表示名。定型の値はラベルに変換し、自由入力はそのまま返す */
export const getSoundReviewTypeLabel = (type: string): string => (
  SOUND_REVIEW_TYPE_LABELS[type as TSoundReviewType] || type
)

/** 保存値の配列を表示名の配列にする。空文字は除く */
export const getSoundReviewTypeLabels = (types?: string[]): string[] => (
  (Array.isArray(types) ? types : [])
    .map(getSoundReviewTypeLabel)
    .filter(Boolean)
)

/**
 * クリエイターが面談で使える通話ツール。
 * 運営（mgmt）が user.soundReviewProfile.callTools に設定し、依頼フォームの選択肢になる。
 */
export const SOUND_REVIEW_CALL_TOOLS = [
  'zoom',
  'google_meet',
  'discord',
  'line',
] as const

export type TSoundReviewCallTool = typeof SOUND_REVIEW_CALL_TOOLS[number]

/** 通話ツールの表示名。DB には英語の値を保存し、画面表示ではこの対応表を使う */
export const SOUND_REVIEW_CALL_TOOL_LABELS: Record<TSoundReviewCallTool, string> = {
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  discord: 'Discord',
  line: 'LINE通話',
}

/** 設定された通話ツールの表示名。定義外の値は無視する */
export const getSoundReviewCallToolLabels = (callTools?: TSoundReviewCallTool[]): string[] => (
  (Array.isArray(callTools) ? callTools : [])
    .map((tool) => SOUND_REVIEW_CALL_TOOL_LABELS[tool])
    .filter(Boolean)
)

/** 全通話ツールの表示名。クリエイターが通話ツールを設定していないときの選択肢 */
export const SOUND_REVIEW_ALL_CALL_TOOL_LABELS = SOUND_REVIEW_CALL_TOOLS
  .map((tool) => SOUND_REVIEW_CALL_TOOL_LABELS[tool])

/**
 * 依頼フォームの面談ツールの選択肢。
 * - 設定済み（isCreatorSpecified: true）: 運営が設定したそのツールだけを選択肢にする
 * - 未設定: 全ツールを選択肢にする
 * どちらの場合も、依頼者は選択肢以外のツール名を自由入力で追加できる。
 */
export const getSoundReviewCallToolChoices = (
  callTools?: TSoundReviewCallTool[],
): { options: string[], isCreatorSpecified: boolean } => {
  const labels = getSoundReviewCallToolLabels(callTools)
  return labels.length
    ? { options: labels, isCreatorSpecified: true }
    : { options: SOUND_REVIEW_ALL_CALL_TOOL_LABELS, isCreatorSpecified: false }
}
